import React, { useEffect } from 'react';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '../innovher/PageHeader';
import { EditorialIntro, IntroSplit } from '../innovher/SplitSection';
import ApplyCta from '../innovher/ApplyCta';
import Magnetic from '../innovher/Magnetic';
import SEO from '../SEO';
import { STOCK } from '../../lib/images';

/** External InnovVeda site */
export const INNOVEDA_SITE = 'https://innoveda.ai';

const FOCUS_AREAS = [
  'Product architecture',
  'Platform development',
  'AI integration',
  'Automation',
  'Data systems',
  'Emerging technologies',
];

function SectionEyebrow({ children }) {
  return (
    <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">{children}</div>
  );
}

function VisitInnovVeda({ size = 'md', className = '' }) {
  const sizes = {
    sm: 'pl-5 pr-1.5 py-1.5 text-sm',
    md: 'pl-7 pr-2 py-2.5 text-base',
    lg: 'pl-8 pr-2.5 py-3 text-base sm:text-lg',
  };
  const icon = size === 'sm' ? 'w-7 h-7' : size === 'lg' ? 'w-10 h-10' : 'w-9 h-9';

  return (
    <Magnetic>
      <a
        href={INNOVEDA_SITE}
        target="_blank"
        rel="noopener noreferrer"
        data-cursor="hover"
        className={`group inline-flex items-center gap-2 rounded-full bg-[#d42b92] text-white font-medium shadow-[0_15px_50px_-10px_rgba(212,43,146,0.55)] hover:bg-white hover:text-black transition-colors duration-300 ${sizes[size]} ${className}`}
      >
        Visit InnovVeda.ai
        <span
          className={`${icon} rounded-full bg-white text-[#d42b92] grid place-items-center group-hover:bg-[#d42b92] group-hover:text-white group-hover:rotate-45 transition`}
        >
          <ArrowUpRight className="w-4 h-4" />
        </span>
      </a>
    </Magnetic>
  );
}

export default function InnovVedaPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SEO
        path="/innoveda"
        title="InnovVeda.ai | Technology to Scale and Expand Horizons"
        description="InnovVeda.ai is InnovHer's technology and AI practice, helping ventures turn business ideas into intelligent, scalable digital products and infrastructure."
        keywords="InnovVeda.ai, AI development, venture technology, product architecture, platform development, InnovHer"
      />

      {/* Header / Purpose Section */}
      <section className="relative bg-[#08080a] pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 lg:pb-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <EditorialIntro
            className="reveal-up"
            eyebrow="InnovVeda.ai"
            logo="/new_images/logos/innovveda.webp"
            title={
              <>
                Technology to scale and expand<span className="text-[#d42b92]"> horizons</span>.
              </>
            }
            lead="Technology and AI development team, translating business ideas into intelligent, scalable digital products and infrastructure."
          >
            <p>
              InnovVeda.ai is technology and AI development team, enabling ventures to translate
              business ideas into intelligent, scalable digital products and infrastructure.
            </p>
            <p>
              From product architecture and platform development to AI integration, automation, data
              systems and emerging technologies, InnovVeda.ai approaches technology through the lens
              of business outcomes rather than technology alone.
            </p>
          </EditorialIntro>
        </div>
      </section>

      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/2 right-0 w-[420px] h-[420px] rounded-full bg-[#d42b92]/10 blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Text content */}
            <div className="reveal-up">
              <SectionEyebrow>How we work</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Technology through the lens of{' '}
                <span className="text-[#d42b92]">business outcomes</span>
              </h2>
              <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed">
                Working alongside founders and InnovHer’s venture-building platforms, it helps
                determine what should be built, why it matters and how technology can create meaningful
                advantage.
              </p>
              <p className="mt-4 text-white font-semibold text-lg">
                The ambition is simple: make technology a strategic capability of the venture -{' '}
                <span className="text-[#ff8ec5] italic">not merely a service supporting it.</span>
              </p>

              <div className="mt-8 flex flex-wrap gap-2.5">
                {FOCUS_AREAS.map((label) => (
                  <span
                    key={label}
                    className="px-4 py-2.5 rounded-full text-sm border border-white/10 bg-white/[0.03] text-white/75"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Image */}
            <div className="reveal-up relative">
              <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
                <img
                  src="/Innovher-home page/IMG_5117.webp"
                  alt="InnovVeda technology and AI development"
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#d42b92]/20 to-transparent pointer-events-none" />
              </div>
              <div className="absolute -inset-4 -z-10 rounded-3xl bg-[#d42b92]/8 blur-2xl pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-[#08080a] pt-4 pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up rounded-[2rem] border border-[#d42b92]/30 bg-gradient-to-br from-[#d42b92]/20 via-black to-black p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-[420px] h-[420px] bg-[#d42b92]/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="relative z-10 max-w-2xl">
              <SectionEyebrow>InnovVeda.ai</SectionEyebrow>
              <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight">
                Explore the InnovVeda website
              </h2>
              <p className="mt-4 text-white/65 text-lg leading-relaxed">
                Visit InnovVeda.ai for the full technology and AI practice, or start a conversation
                with InnovHer to build together.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <VisitInnovVeda size="lg" />
                <ApplyCta
                  to="/contact"
                  label="Contact Us"
                  size="md"
                  variant="secondary"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
